/**
 * A client-readable hint about whether this browser has a session cookie.
 *
 * The marketing header is rendered from statically prerendered HTML (the root
 * layout must never read cookies()/headers() — that would opt every route out of
 * static generation), so the server HTML contains neither the "Dashboard" nor the
 * "Login" link. The client used to learn which one to show only from
 * /api/auth/check-session, which on a cold visit answered in ~2.3s. First-time
 * visitors therefore saw an auth-less navbar, and a signed-in user only got the
 * Dashboard link on a *later* page load, once localStorage had cached the answer.
 *
 * The proxy already knows the answer on every document request — it inspects the
 * cookie jar for a session cookie. It writes that single bit here so the header
 * can render the right link the moment it hydrates, without waiting on the probe.
 *
 * This grants NOTHING. It decides which link is painted, nothing else; every
 * dashboard route and API authenticates the real session server-side. The value
 * is refreshed by the proxy on every document request and confirmed by the probe
 * a moment later, so a stale hint corrects itself.
 */

export const SESSION_HINT_COOKIE = 'sv-auth-hint';
export const SESSION_HINT_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

/**
 * @returns true/false when the proxy has stated an answer for this browser,
 *          null when there is no hint to go on (treat as "not signed in", but
 *          let a cached answer fill the gap first).
 */
export function readSessionHint(cookieHeader: string | null | undefined): boolean | null {
  if (!cookieHeader) return null;

  for (const part of cookieHeader.split(';')) {
    const separator = part.indexOf('=');
    if (separator === -1) continue;

    if (part.slice(0, separator).trim() !== SESSION_HINT_COOKIE) continue;

    const value = part.slice(separator + 1).trim();
    if (value === '1') return true;
    if (value === '0') return false;
    return null;
  }

  return null;
}

/**
 * What the header should paint the instant it hydrates, before the probe answers.
 *
 * The hint outranks the localStorage cache: it comes from the cookies sent with
 * *this* request, so it is still right after a sign-out in another tab, while the
 * cache is only ever as fresh as this browser's last probe. The cache covers the
 * case where no hint was written (a response that never passed through the proxy).
 */
export function initialAuthState(hint: boolean | null, cachedAuth: boolean): boolean {
  return hint ?? cachedAuth;
}

/**
 * The cookie value that states the answer.
 *
 * The proxy writes this and `readSessionHint` parses it, so the encoding has a
 * single owner and the two halves cannot drift apart.
 */
export function sessionHintValue(hasSession: boolean): string {
  return hasSession ? '1' : '0';
}
